// Centre-pane section editor. Renders the skeleton for whichever
// section is selected in the left rail. For Module 1 every section is
// a flat list of <ParamField> rows; Modules 2-7 will replace individual
// sections with richer editors (airframe XML, DEM tile picker, etc.).
//
// Module 2: when `activeSection === "airframe"` and the scenario has
// one or more airframe_ids attached, switch to the full-fidelity
// <AirframeEditor> for the first attached airframe. Until Module 2 the
// airframe section shows its generic <ParamField> skeleton.
import { useEffect, useState } from "react";
import ParamField from "@/components/ParamField";
import EmptyState from "@/components/EmptyState";
import AirframeEditor from "@/components/airframe/AirframeEditor";
import EnvironmentEditor from "@/components/environment/EnvironmentEditor";
import WorkbenchTerrainPane from "@/components/terrain/WorkbenchTerrainPane";
import WorkbenchThreatsPane from "@/components/threats/WorkbenchThreatsPane";
import WorkbenchInterceptorPane from "@/components/interceptor/WorkbenchInterceptorPane";
import WorkbenchEngagementPane from "@/components/engagement/WorkbenchEngagementPane";
import WorkbenchAttachSelect from "@/components/workbench/WorkbenchAttachSelect";
import { sectionById } from "@/components/workbench/sections";
import { sim } from "@/api/endpoints";

function paramFromTree(scenario, sectionId, name) {
    return scenario?.parameters?.[sectionId]?.[name] || null;
}

export default function WorkbenchEditor({
    scenario,
    activeSection,
    onParameterChange,
    onOpenRunFlow,
    onScenarioChanged,
    readOnly = false,
}) {
    const section = sectionById(activeSection);
    const [attachedAirframe, setAttachedAirframe] = useState(null);
    const [attachedEnvironment, setAttachedEnvironment] = useState(null);

    // Module 2: if the scenario has an airframe attached and we're on
    // the airframe section, hydrate the first one and render the
    // AirframeEditor. Re-fetch whenever the scenario's airframe_ids
    // list changes.
    const firstAirframeId = scenario?.airframe_ids?.[0] || null;
    useEffect(() => {
        let cancelled = false;
        if (activeSection !== "airframe" || !firstAirframeId) {
            setAttachedAirframe(null);
            return undefined;
        }
        (async () => {
            try {
                const af = await sim.airframes.get(firstAirframeId);
                if (!cancelled) setAttachedAirframe(af);
            } catch {
                if (!cancelled) setAttachedAirframe(null);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [activeSection, firstAirframeId]);

    // Module 3: hydrate the attached environment when the environment
    // section is active. Scenario carries a single environment_id (vs
    // the airframes list); we fetch it lazily.
    const environmentId = scenario?.environment_id || null;
    useEffect(() => {
        let cancelled = false;
        if (activeSection !== "environment" || !environmentId) {
            setAttachedEnvironment(null);
            return undefined;
        }
        (async () => {
            try {
                const env = await sim.environments.get(environmentId);
                if (!cancelled) setAttachedEnvironment(env);
            } catch {
                if (!cancelled) setAttachedEnvironment(null);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [activeSection, environmentId]);

    if (!section) {
        return (
            <div className="h-full p-6" data-testid="workbench-editor-empty">
                <EmptyState
                    title="Pick a section"
                    body="Choose an item from the left rail to edit its parameters."
                />
            </div>
        );
    }

    const Icon = section.icon;

    return (
        <section
            className="h-full overflow-y-auto"
            aria-labelledby="workbench-section-title"
            data-testid={`workbench-editor-${section.id}`}
        >
            <header className="sticky top-0 z-10 bg-ux-bg-1 border-b border-ux-border-1 px-6 py-4">
                <div className="flex items-center gap-2">
                    <Icon className="w-4 h-4 text-ux-accent" aria-hidden="true" />
                    <h2
                        id="workbench-section-title"
                        className="text-[14px] font-medium text-ux-text-1"
                    >
                        {section.label}
                    </h2>
                </div>
                <p className="mt-1 text-[12px] text-ux-text-2">{section.description}</p>
                <p
                    className="mt-2 text-[11px] font-mono uppercase tracking-wider text-ux-text-3"
                    data-testid={`workbench-editor-backend-hint-${section.id}`}
                >
                    {section.backendHint}
                </p>
            </header>

            <div className="p-6">
                {section.id === "airframe" && attachedAirframe ? (
                    <AirframeEditor
                        airframe={attachedAirframe}
                        readOnly={readOnly}
                        onChange={(next) => setAttachedAirframe(next)}
                        onAfterVerify={(fresh) => {
                            setAttachedAirframe(fresh);
                            onScenarioChanged?.();
                        }}
                    />
                ) : section.id === "airframe" ? (
                    <WorkbenchAttachSelect
                        sectionLabel="Airframe"
                        placeholder="— pick an airframe —"
                        listFn={() => sim.airframes.list({ scope: "tenant" })}
                        onAttach={(airframeId) =>
                            sim.scenarios.attachAirframe(scenario.id, {
                                airframe_id: airframeId,
                                role: "interceptor",
                            })
                        }
                        libraryPath="/plan/airframes"
                        emptyTitle="No airframe attached"
                        emptyBody="Pick one from the library to edit its aero table, control surfaces, and run the canned Verification Flight."
                        testIdPrefix="workbench-airframe"
                        readOnly={readOnly}
                        onAttached={onScenarioChanged}
                    />
                ) : section.id === "environment" && attachedEnvironment ? (
                    <EnvironmentEditor
                        environment={attachedEnvironment}
                        readOnly={readOnly}
                        onChange={(next) => {
                            setAttachedEnvironment(next);
                            onScenarioChanged?.();
                        }}
                    />
                ) : section.id === "environment" ? (
                    <WorkbenchAttachSelect
                        sectionLabel="Environment"
                        placeholder="— pick an environment —"
                        listFn={() => sim.environments.list({ scope: "tenant" })}
                        onAttach={(environmentId) =>
                            sim.scenarios.attachEnvironment(scenario.id, {
                                environment_id: environmentId,
                            })
                        }
                        libraryPath="/plan/environments"
                        emptyTitle="No environment attached"
                        emptyBody="Attach a preset or tenant-owned environment — wind, turbulence, visibility, and solar all flow through into the seeker chain."
                        testIdPrefix="workbench-environment"
                        readOnly={readOnly}
                        onAttached={onScenarioChanged}
                    />
                ) : section.id === "terrain" ? (
                    <WorkbenchTerrainPane
                        scenario={scenario}
                        readOnly={readOnly}
                        onScenarioChanged={onScenarioChanged}
                    />
                ) : section.id === "threats" ? (
                    <WorkbenchThreatsPane
                        scenario={scenario}
                        readOnly={readOnly}
                        onScenarioChanged={onScenarioChanged}
                    />
                ) : section.id === "interceptor" ? (
                    <WorkbenchInterceptorPane
                        scenario={scenario}
                        readOnly={readOnly}
                        onScenarioChanged={onScenarioChanged}
                    />
                ) : section.id === "engagement" ? (
                    <WorkbenchEngagementPane
                        scenario={scenario}
                        readOnly={readOnly}
                        onScenarioChanged={onScenarioChanged}
                    />
                ) : section.id === "runs" ? (
                    <div className="flex flex-col gap-4" data-testid="workbench-runs-pane">
                        <EmptyState
                            title="No runs yet for this scenario"
                            body="Click Run in the scenario header to submit a Monte Carlo against this scenario. Every run back-links here automatically."
                        />
                    </div>
                ) : section.fields.length === 0 ? (
                    <EmptyState
                        title="No parameters in this section yet"
                        body="Later modules will fill this in."
                    />
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
                        {section.fields.map((f) => {
                            const p = paramFromTree(scenario, section.id, f.name);
                            return (
                                <ParamField
                                    key={f.name}
                                    id={`${section.id}-${f.name}`}
                                    label={f.label}
                                    description={f.description}
                                    kind={f.kind}
                                    value={p ? p.value : null}
                                    source={p?.source}
                                    confidence={p?.confidence}
                                    bounds={p?.bounds || f.bounds}
                                    citation={p?.citation}
                                    placeholder={f.placeholder}
                                    readOnly={readOnly}
                                    onChange={(si, unit) =>
                                        onParameterChange?.({
                                            section: section.id,
                                            name: f.name,
                                            value: si,
                                            unit,
                                        })
                                    }
                                    testId={`paramfield-${section.id}-${f.name}`}
                                />
                            );
                        })}
                    </div>
                )}
            </div>
        </section>
    );
}
